// src/pages/ERAPosting.js
// ERA / Electronic Remittance Advice — post insurance payments and apply to claims

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth, useDemoTour } from '../contexts';
import * as awsApi from '../services/awsApiService';
import * as apiService from '../services/apiService';
import { demoERAPayments } from '../data/demoData';
import {
  Plus, DollarSign, CheckCircle, Clock, AlertTriangle,
  ChevronDown, ChevronUp, RefreshCw, FileText, X, Zap, Link2
} from 'lucide-react';

const STATUS_COLORS = {
  Pending:  'bg-amber-100 text-amber-800 border-amber-200',
  Applied:  'bg-green-100 text-green-800 border-green-200',
  Partial:  'bg-blue-100 text-blue-800 border-blue-200',
  Rejected: 'bg-red-100 text-red-800 border-red-200',
};

const formatCurrency = (v) =>
  v != null ? `$${parseFloat(v).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '—';

const formatDate = (d) => d ? new Date(d).toLocaleDateString() : '—';

export default function ERAPosting() {
  const navigate = useNavigate();
  const { practice } = useAuth();
  const { currentStep } = useDemoTour() || {};
  const [payments, setPayments] = useState([]);
  const [achPhase, setAchPhase] = useState('confirmed'); // 'idle' | 'sending' | 'confirmed'
  const [loading, setLoading] = useState(true);
  const [showNewForm, setShowNewForm] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  const [applyingId, setApplyingId] = useState(null);
  const [toast, setToast] = useState(null);
  const [claims, setClaims] = useState([]);
  const [syncing, setSyncing] = useState(false);
  const [matchModal, setMatchModal] = useState(null); // { eraId, exceptionRef, billedAmount }
  const [matchClaimId, setMatchClaimId] = useState('');
  const [matchAmount, setMatchAmount] = useState('');
  const [matchSaving, setMatchSaving] = useState(false);

  const [form, setForm] = useState({
    payer_name: '', check_number: '', payment_date: new Date().toISOString().split('T')[0],
    total_amount: '', payment_method: 'EFT', notes: '', claim_adjustments: []
  });
  const [adjRow, setAdjRow] = useState({ claim_id: '', paid_amount: '', adjustment_reason: '' });

  const showToast = (msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 4000);
  };

  const load = useCallback(async () => {
    setLoading(true);
    if (apiService.getAppMode() === 'demo') {
      setPayments(demoERAPayments);
      setLoading(false);
      return;
    }
    const [eraRes, claimsRes] = await Promise.all([
      awsApi.getERAPayments(),
      awsApi.getClaims(practice?.practice_id, { limit: 200 })
    ]);
    if (eraRes.success) setPayments(eraRes.data);
    if (claimsRes.success) setClaims(claimsRes.data.filter(c => c.claim_status !== 'Paid'));
    setLoading(false);
  }, [practice]);

  useEffect(() => { load(); }, [load]);

  // Demo tour: ERA posting step — animate ACH payment link being sent for Sarah's payment
  useEffect(() => {
    if (apiService.getAppMode() !== 'demo') return;
    const id = currentStep?.id;
    if (id !== 'era-posting' && id !== 'workflow-payment') {
      setAchPhase('confirmed'); // static badge on all other steps
      return;
    }
    setAchPhase('idle');
    const t1 = setTimeout(() => setAchPhase('sending'), 1500);
    const t2 = setTimeout(() => setAchPhase('confirmed'), 3800);
    return () => { clearTimeout(t1); clearTimeout(t2); setAchPhase('confirmed'); };
  }, [currentStep?.id]); // eslint-disable-line

  const handleAddAdj = () => {
    if (!adjRow.claim_id || !adjRow.paid_amount) return;
    setForm(f => ({ ...f, claim_adjustments: [...f.claim_adjustments, { ...adjRow }] }));
    setAdjRow({ claim_id: '', paid_amount: '', adjustment_reason: '' });
  };

  const handleRemoveAdj = (i) => {
    setForm(f => ({ ...f, claim_adjustments: f.claim_adjustments.filter((_, idx) => idx !== i) }));
  };

  const handleSubmit = async () => {
    if (!form.payer_name || !form.total_amount) return showToast('Payer name and amount are required', 'error');
    const res = await awsApi.postERAPayment(form);
    if (res.success) {
      showToast('ERA payment recorded');
      setShowNewForm(false);
      setForm({ payer_name: '', check_number: '', payment_date: new Date().toISOString().split('T')[0], total_amount: '', payment_method: 'EFT', notes: '', claim_adjustments: [] });
      load();
    } else {
      showToast(res.error, 'error');
    }
  };

  const handleSyncERA = async () => {
    setSyncing(true);
    const res = await awsApi.syncERAFromClearinghouse();
    setSyncing(false);
    if (res.success) {
      const d = res.data;
      showToast(`Sync complete — ${d.matched || 0} matched, ${d.exceptions || 0} exceptions, ${d.files_processed || 0} file(s)`);
      load();
    } else {
      showToast(res.error || 'Sync failed', 'error');
    }
  };

  const handleApply = async (id) => {
    setApplyingId(id);
    const res = await awsApi.applyERAPayment(id);
    setApplyingId(null);
    if (res.success) {
      showToast(`Applied to ${res.appliedCount} claim(s) — ${formatCurrency(res.appliedTotal)}`);
      load();
    } else {
      showToast(res.error || 'Failed to apply', 'error');
    }
  };

  const handleResolveException = async () => {
    if (!matchClaimId || !matchAmount) return;
    setMatchSaving(true);
    const res = await awsApi.resolveERAException(matchModal.eraId, matchClaimId, parseFloat(matchAmount), matchModal.exceptionRef);
    setMatchSaving(false);
    if (res.success) {
      showToast('Exception resolved — claim posted');
      setMatchModal(null);
      setMatchClaimId('');
      setMatchAmount('');
      load();
    } else {
      showToast(res.error || 'Failed to resolve exception', 'error');
    }
  };

  const totalPending = payments.filter(p => p.status === 'Pending').reduce((s, p) => s + parseFloat(p.total_amount || 0), 0);
  const totalApplied = payments.filter(p => p.status === 'Applied').reduce((s, p) => s + parseFloat(p.applied_amount || 0), 0);
  const totalExceptions = payments.filter(p => p.status === 'Exception' || p.status === 'Partial').length;
  const exceptionAmount = payments
    .filter(p => p.status === 'Exception' || p.status === 'Partial')
    .reduce((s, p) => {
      const adjs = p.claim_adjustments || [];
      return s + adjs.filter(a => a.status === 'exception').reduce((sum, a) => sum + parseFloat(a.paid_amount || a.billed || 0), 0);
    }, 0);

  return (
    <div data-tour="era-content" className="min-h-screen bg-slate-50">
      {/* Toast */}
      {toast && (
        <div className={`fixed top-4 right-4 z-50 px-4 py-3 rounded-lg shadow-lg text-sm font-medium flex items-center gap-2
          ${toast.type === 'error' ? 'bg-red-600 text-white' : 'bg-green-600 text-white'}`}>
          {toast.type === 'error' ? <AlertTriangle size={16} /> : <CheckCircle size={16} />}
          {toast.msg}
        </div>
      )}

      {/* Header */}
      <div className="bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-teal-100 rounded-lg flex items-center justify-center">
              <DollarSign size={18} className="text-teal-700" />
            </div>
            <div>
              <h1 className="text-lg font-semibold text-slate-900">ERA Payment Posting</h1>
              <p className="text-xs text-slate-500">Post insurance payments and apply to claims</p>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={load} className="p-2 text-slate-500 hover:text-slate-700 hover:bg-slate-100 rounded-lg">
            <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
          </button>
          <button
            onClick={handleSyncERA}
            disabled={syncing}
            className="flex items-center gap-2 bg-slate-700 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-slate-800 disabled:opacity-50"
          >
            <RefreshCw size={16} className={syncing ? 'animate-spin' : ''} />
            {syncing ? 'Syncing...' : 'Sync ERA'}
          </button>
          <button
            onClick={() => setShowNewForm(true)}
            className="flex items-center gap-2 bg-teal-700 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-teal-800"
          >
            <Plus size={16} /> Post ERA Payment
          </button>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-6 space-y-6">
        {/* Summary Cards */}
        <div className="grid grid-cols-4 gap-4">
          <div className="bg-white rounded-xl border border-slate-200 p-5">
            <p className="text-xs text-slate-500 font-medium uppercase tracking-wide">Pending ERA</p>
            <p className="text-2xl font-bold text-amber-600 mt-1">{formatCurrency(totalPending)}</p>
            <p className="text-xs text-slate-400 mt-1">{payments.filter(p => p.status === 'Pending').length} awaiting application</p>
          </div>
          <div className="bg-white rounded-xl border border-slate-200 p-5">
            <p className="text-xs text-slate-500 font-medium uppercase tracking-wide">Applied</p>
            <p className="text-2xl font-bold text-green-600 mt-1">{formatCurrency(totalApplied)}</p>
            <p className="text-xs text-slate-400 mt-1">{payments.filter(p => p.status === 'Applied').length} payments posted</p>
          </div>
          <div className={`bg-white rounded-xl border p-5 ${totalExceptions > 0 ? 'border-amber-300' : 'border-slate-200'}`}>
            <p className={`text-xs font-medium uppercase tracking-wide ${totalExceptions > 0 ? 'text-amber-600' : 'text-slate-500'}`}>Unmatched Exceptions</p>
            <p className={`text-2xl font-bold mt-1 ${totalExceptions > 0 ? 'text-amber-600' : 'text-slate-400'}`}>{totalExceptions > 0 ? formatCurrency(exceptionAmount) : '—'}</p>
            <p className={`text-xs mt-1 ${totalExceptions > 0 ? 'text-amber-500' : 'text-slate-400'}`}>{totalExceptions > 0 ? `${totalExceptions} ERA batch(es) need manual match` : 'No exceptions'}</p>
          </div>
          <div className="bg-white rounded-xl border border-slate-200 p-5">
            <p className="text-xs text-slate-500 font-medium uppercase tracking-wide">Total ERA Payments</p>
            <p className="text-2xl font-bold text-slate-700 mt-1">{payments.length}</p>
            <p className="text-xs text-slate-400 mt-1">All time</p>
          </div>
        </div>

        {/* Match Exception Modal */}
        {matchModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={() => setMatchModal(null)}>
            <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-6" onClick={e => e.stopPropagation()}>
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-semibold text-slate-900 flex items-center gap-2">
                  <Link2 size={16} className="text-teal-600" /> Match Exception to Claim
                </h2>
                <button onClick={() => setMatchModal(null)} className="text-slate-400 hover:text-slate-600"><X size={18} /></button>
              </div>
              <p className="text-sm text-slate-500 mb-1">Unmatched ref: <span className="font-mono text-slate-700">{matchModal.exceptionRef || '—'}</span></p>
              {matchModal.billedAmount > 0 && <p className="text-sm text-slate-500 mb-4">Billed: <span className="font-semibold">{formatCurrency(matchModal.billedAmount)}</span></p>}
              <div className="space-y-3 mb-5">
                <div>
                  <label className="block text-xs font-medium text-slate-700 mb-1">Select Matching Claim *</label>
                  <select
                    className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm"
                    value={matchClaimId}
                    onChange={e => setMatchClaimId(e.target.value)}
                  >
                    <option value="">— Select claim —</option>
                    {claims.filter(c => !['Paid','Applied','Written Off'].includes(c.claim_status)).map(c => (
                      <option key={c.id} value={c.id}>
                        {c.patient_name || c.patient_id} — {Array.isArray(c.procedure_codes) ? c.procedure_codes.join(',') : c.procedure_codes} — {formatCurrency(c.claim_amount)}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-700 mb-1">Payment Amount *</label>
                  <input
                    type="number" step="0.01"
                    className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm"
                    value={matchAmount}
                    onChange={e => setMatchAmount(e.target.value)}
                    placeholder={matchModal.billedAmount > 0 ? String(matchModal.billedAmount) : '0.00'}
                  />
                </div>
              </div>
              <div className="flex justify-end gap-2">
                <button onClick={() => setMatchModal(null)} className="px-4 py-2 text-sm text-slate-600 border border-slate-300 rounded-lg hover:bg-slate-50">Cancel</button>
                <button
                  onClick={handleResolveException}
                  disabled={matchSaving || !matchClaimId || !matchAmount}
                  className="px-4 py-2 text-sm bg-teal-700 text-white rounded-lg hover:bg-teal-800 font-medium disabled:opacity-50"
                >
                  {matchSaving ? 'Saving...' : 'Post & Resolve'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* New ERA Form */}
        {showNewForm && (
          <div className="bg-white rounded-xl border border-teal-200 shadow-sm">
            <div className="p-5 border-b border-slate-100 flex items-center justify-between">
              <h2 className="font-semibold text-slate-800 flex items-center gap-2">
                <Plus size={16} className="text-teal-600" /> New ERA Payment
              </h2>
              <button onClick={() => setShowNewForm(false)} className="text-slate-400 hover:text-slate-600">
                <X size={18} />
              </button>
            </div>
            <div className="p-5 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-slate-700 mb-1">Payer Name *</label>
                  <input className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm"
                    value={form.payer_name} onChange={e => setForm(f => ({ ...f, payer_name: e.target.value }))}
                    placeholder="e.g. Delta Dental" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-700 mb-1">Check / EFT Number</label>
                  <input className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm"
                    value={form.check_number} onChange={e => setForm(f => ({ ...f, check_number: e.target.value }))}
                    placeholder="e.g. 12345678" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-700 mb-1">Payment Date</label>
                  <input type="date" className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm"
                    value={form.payment_date} onChange={e => setForm(f => ({ ...f, payment_date: e.target.value }))} />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-700 mb-1">Total Amount *</label>
                  <input type="number" step="0.01" className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm"
                    value={form.total_amount} onChange={e => setForm(f => ({ ...f, total_amount: e.target.value }))}
                    placeholder="0.00" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-700 mb-1">Payment Method</label>
                  <select className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm"
                    value={form.payment_method} onChange={e => setForm(f => ({ ...f, payment_method: e.target.value }))}>
                    <option>EFT</option>
                    <option>Check</option>
                    <option>Virtual Card</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-700 mb-1">Notes</label>
                  <input className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm"
                    value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
                    placeholder="Optional notes" />
                </div>
              </div>

              {/* Claim Adjustments */}
              <div className="border border-slate-200 rounded-lg p-4">
                <h3 className="text-sm font-semibold text-slate-700 mb-3">Claim Adjustments (optional)</h3>
                <p className="text-xs text-slate-500 mb-3">Link specific claim IDs to payment amounts for automatic posting.</p>
                <div className="grid grid-cols-3 gap-2 mb-2">
                  <select className="border border-slate-300 rounded-lg px-3 py-2 text-sm"
                    value={adjRow.claim_id} onChange={e => setAdjRow(r => ({ ...r, claim_id: e.target.value }))}>
                    <option value="">Select Claim</option>
                    {claims.map(c => (
                      <option key={c.id} value={c.id}>
                        {c.patient_name || c.patient_id} — {c.procedure_codes} — {formatCurrency(c.claim_amount)}
                      </option>
                    ))}
                  </select>
                  <input type="number" step="0.01" className="border border-slate-300 rounded-lg px-3 py-2 text-sm"
                    value={adjRow.paid_amount} onChange={e => setAdjRow(r => ({ ...r, paid_amount: e.target.value }))}
                    placeholder="Paid amount" />
                  <button onClick={handleAddAdj}
                    className="bg-teal-50 text-teal-700 border border-teal-200 rounded-lg px-3 py-2 text-sm font-medium hover:bg-teal-100">
                    + Add
                  </button>
                </div>
                {form.claim_adjustments.length > 0 && (
                  <div className="space-y-1 mt-2">
                    {form.claim_adjustments.map((adj, i) => {
                      const c = claims.find(cl => cl.id === adj.claim_id);
                      return (
                        <div key={i} className="flex items-center justify-between bg-slate-50 rounded px-3 py-2 text-sm">
                          <span className="text-slate-700">{c ? `${c.patient_name || c.patient_id} — ${c.procedure_codes}` : adj.claim_id}</span>
                          <div className="flex items-center gap-3">
                            <span className="font-semibold text-green-700">{formatCurrency(adj.paid_amount)}</span>
                            <button onClick={() => handleRemoveAdj(i)} className="text-slate-400 hover:text-red-500"><X size={14} /></button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              <div className="flex justify-end gap-2 pt-2">
                <button onClick={() => setShowNewForm(false)}
                  className="px-4 py-2 text-sm text-slate-600 border border-slate-300 rounded-lg hover:bg-slate-50">
                  Cancel
                </button>
                <button onClick={handleSubmit}
                  className="px-4 py-2 text-sm bg-teal-700 text-white rounded-lg hover:bg-teal-800 font-medium">
                  Post Payment
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Payments List */}
        <div className="bg-white rounded-xl border border-slate-200">
          <div className="p-5 border-b border-slate-100">
            <h2 className="font-semibold text-slate-800">ERA Payments</h2>
          </div>
          {loading ? (
            <div className="p-12 text-center text-slate-500">
              <RefreshCw size={24} className="animate-spin mx-auto mb-2 text-teal-600" />
              Loading payments...
            </div>
          ) : payments.length === 0 ? (
            <div className="p-12 text-center">
              <FileText size={32} className="mx-auto text-slate-300 mb-3" />
              <p className="text-slate-500 font-medium">No ERA payments posted yet</p>
              <p className="text-slate-400 text-sm mt-1">Post your first ERA payment to start tracking</p>
              <button onClick={() => setShowNewForm(true)}
                className="mt-4 bg-teal-700 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-teal-800">
                Post ERA Payment
              </button>
            </div>
          ) : (
            <div className="divide-y divide-slate-100">
              {payments.map(p => (
                <div key={p.id}>
                  <div
                    data-tour={p.id === 'era-demo-002' ? 'sarah-era-payment' : undefined}
                    className="px-5 py-4 flex items-center justify-between hover:bg-slate-50 cursor-pointer"
                    onClick={() => setExpandedId(expandedId === p.id ? null : p.id)}
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 bg-teal-50 rounded-lg flex items-center justify-center">
                        <DollarSign size={18} className="text-teal-700" />
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <p className="font-medium text-slate-800">{p.payer_name}</p>
                          {p.source === 'auto' && (
                            <span className="text-xs bg-teal-100 text-teal-700 px-2 py-0.5 rounded-full font-medium">Auto-Synced</span>
                          )}
                          {p.id === 'era-demo-002' && achPhase === 'sending' && (
                            <span className="text-xs text-slate-500 flex items-center gap-1.5">
                              <RefreshCw size={10} className="animate-spin text-emerald-500" />
                              Sending payment link to patient...
                            </span>
                          )}
                          {p.id === 'era-demo-002' && achPhase === 'confirmed' && (
                            <span
                              key={achPhase + currentStep?.id}
                              className="text-xs bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full font-medium"
                              style={{ animation: 'demoBadgeIn 0.55s ease-out, pulseGlowGreen 2s ease-in-out 0.55s infinite' }}
                            >✓ Payment link sent to patient</span>
                          )}
                        </div>
                        <p className="text-xs text-slate-500">
                          {p.check_number ? `#${p.check_number} · ` : ''}{p.payment_method} · {formatDate(p.payment_date)}
                          {p.era_filename ? ` · ${p.era_filename}` : ''}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="text-right">
                        <p className="font-bold text-slate-800">{formatCurrency(p.total_amount)}</p>
                        {parseFloat(p.applied_amount) > 0 && (
                          <p className="text-xs text-green-600">{formatCurrency(p.applied_amount)} applied</p>
                        )}
                      </div>
                      <span className={`text-xs px-2 py-1 rounded-full border font-medium ${STATUS_COLORS[p.status] || 'bg-slate-100 text-slate-600'}`}>
                        {p.status}
                      </span>
                      {p.status === 'Pending' && (p.claim_adjustments || []).length > 0 && (
                        <button
                          onClick={(e) => { e.stopPropagation(); handleApply(p.id); }}
                          disabled={applyingId === p.id}
                          className="flex items-center gap-1 bg-teal-700 text-white px-3 py-1.5 rounded-lg text-xs font-medium hover:bg-teal-800 disabled:opacity-50"
                        >
                          {applyingId === p.id ? <RefreshCw size={12} className="animate-spin" /> : <Zap size={12} />}
                          Apply to Claims
                        </button>
                      )}
                      {expandedId === p.id ? <ChevronUp size={16} className="text-slate-400" /> : <ChevronDown size={16} className="text-slate-400" />}
                    </div>
                  </div>
                  {expandedId === p.id && (
                    <div className="px-5 pb-4 bg-slate-50 border-t border-slate-100">
                      <div className="mt-3 grid grid-cols-2 gap-4 text-sm">
                        <div><span className="text-slate-500">Payment Method:</span> <span className="font-medium">{p.payment_method}</span></div>
                        <div><span className="text-slate-500">Posted:</span> <span className="font-medium">{formatDate(p.created_at)}</span></div>
                        {p.notes && <div className="col-span-2"><span className="text-slate-500">Notes:</span> <span>{p.notes}</span></div>}
                      </div>
                      {((p.adjustments || p.claim_adjustments || []).length > 0) && (
                        <div className="mt-3">
                          <p className="text-xs font-semibold text-slate-600 uppercase tracking-wide mb-2">Claim Adjustments</p>
                          <div className="space-y-1">
                            {(p.adjustments || p.claim_adjustments || []).map((adj, i) => {
                              // Support both demo format (adjustments) and DB format (claim_adjustments)
                              const c = adj.claim_id ? claims.find(cl => cl.id === adj.claim_id) : null;
                              const isException = adj.status === 'exception';
                              const patientLabel = adj.patient_name || (c ? (c.patient_name || c.patient_id) : null);
                              const procLabel = adj.procedure_code || (c ? c.procedure_codes : null);
                              const paidAmt = adj.paid ?? adj.paid_amount ?? 0;
                              return (
                                <div key={i} className={`flex justify-between rounded px-3 py-2 text-sm border ${isException ? 'bg-amber-50 border-amber-200' : 'bg-white border-slate-200'}`}>
                                  <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2">
                                      {isException && <AlertTriangle size={12} className="text-amber-500" />}
                                      <span className={`font-medium ${isException ? 'text-amber-700' : 'text-slate-700'}`}>
                                        {patientLabel || (isException ? `Unmatched — ref: ${adj.original_claim_ref || '?'}` : '—')}
                                      </span>
                                      {procLabel && <span className="text-xs text-slate-400">{procLabel}</span>}
                                    </div>
                                    {adj.reason && <p className="text-xs text-slate-400 mt-0.5">{adj.reason}</p>}
                                  </div>
                                  <div className="flex items-center gap-3 ml-4 shrink-0">
                                    <div className="text-right">
                                      <span className={`font-semibold ${isException ? 'text-amber-700' : 'text-green-700'}`}>{formatCurrency(paidAmt)}</span>
                                      {adj.billed && adj.billed !== paidAmt && (
                                        <p className="text-xs text-slate-400">of {formatCurrency(adj.billed)} billed</p>
                                      )}
                                    </div>
                                    {isException && apiService.getAppMode() !== 'demo' && (
                                      <button
                                        onClick={e => {
                                          e.stopPropagation();
                                          setMatchClaimId('');
                                          setMatchAmount(String(paidAmt || adj.billed || ''));
                                          setMatchModal({ eraId: p.id, exceptionRef: adj.original_claim_ref, billedAmount: parseFloat(adj.billed || 0) });
                                        }}
                                        className="flex items-center gap-1 text-xs font-medium text-teal-700 bg-teal-50 border border-teal-200 px-2 py-1 rounded-lg hover:bg-teal-100"
                                      >
                                        <Link2 size={11} /> Match
                                      </button>
                                    )}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
      <style>{`
        @keyframes demoBadgeIn {
          from { opacity: 0; transform: translateY(6px) scale(0.95); }
          to   { opacity: 1; transform: translateY(0)   scale(1); }
        }
        @keyframes pulseGlowGreen {
          0%   { box-shadow: 0 0 0 0   rgba(16,185,129,0.45); }
          70%  { box-shadow: 0 0 0 8px rgba(16,185,129,0); }
          100% { box-shadow: 0 0 0 0   rgba(16,185,129,0); }
        }
        @keyframes pulseGlowPurple {
          0%   { box-shadow: 0 0 0 0    rgba(147,51,234,0.35); }
          70%  { box-shadow: 0 0 0 10px rgba(147,51,234,0); }
          100% { box-shadow: 0 0 0 0    rgba(147,51,234,0); }
        }
      `}</style>
    </div>
  );
}
